"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from "react";
import debounce from "lodash.debounce";
import { useUnmount } from "./useUnmount";
type DebounceOptions = {
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
};
type ControlFunctions = {
  cancel: () => void;
  flush: () => void;
  isPending: () => boolean;
};
export type DebouncedState<T extends (...args: any) => ReturnType<T>> = ((...args: Parameters<T>) => ReturnType<T> | undefined) & ControlFunctions;
export function useDebounceCallback(func, t0, options) {
  const $ = _c(11);
  const delay = t0 === undefined ? 500 : t0;
  const debouncedFunc = useRef(undefined);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (debouncedFunc.current) {
        debouncedFunc.current.cancel();
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useUnmount(t1);
  let wrappedFunc;
  if ($[1] !== delay || $[2] !== func || $[3] !== options) {
    const debouncedFuncInstance = debounce(func, delay, options);
    wrappedFunc = (...t2) => {
      const args = t2;
      return debouncedFuncInstance(...args);
    };
    wrappedFunc.cancel = () => {
      debouncedFuncInstance.cancel();
    };
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = () => !!debouncedFunc.current;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    wrappedFunc.isPending = t3;
    wrappedFunc.flush = () => debouncedFuncInstance.flush();
    $[1] = delay;
    $[2] = func;
    $[3] = options;
    $[4] = wrappedFunc;
  } else {
    wrappedFunc = $[4];
  }
  const debounced = wrappedFunc;
  let t2;
  let t3;
  if ($[6] !== delay || $[7] !== func || $[8] !== options) {
    t2 = () => {
      debouncedFunc.current = debounce(func, delay, options);
    };
    t3 = [func, delay, options];
    $[6] = delay;
    $[7] = func;
    $[8] = options;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  return debounced;
}